import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import {
  AssetType,
  CallableType,
  CollateralBasketType,
  Collection,
  CollectionActions,
  CollectionModifyTypeArgs,
  CollectionType,
  CounterpartyInstruction,
  EntityType,
  Market,
  NegotiationInstruction,
  OrderActions,
  OrderBulkPrepareArgs,
  OrderCounterpartyInstructions,
  OrderEvent,
  OrderPrepareCollectionArgs,
  RepoClassification,
  RepoType,
  SettlementVenue,
  TradeConditionFlag,
  TradingEntity,
  TradingRelationship,
  UserPreference
} from 'api'
import { TextButton, TextButtonType } from 'view/components/buttons/TextButton'
import { DialogField, DialogRow } from 'view/components/inputs'
import { SendRFQPanelTitle } from './SendRFQPanelTitle'
import { AssetsSection } from './AssetsSection'
import { RFQDetailsSection } from './RFQDetailsSection'
import { CounterpartySection } from './CounterpartySection'
import { PositionsSection } from './PositionsSection'
import { Tooltip } from 'view/components/tooltip'
import { MAX_BULK_PREPARE_ACTION_SIZE } from 'model/orderUtils'
import { SelectionActions } from 'store/selection'
import { PreparedStagingOrder } from 'store/selection/selectionTypes'
import {
  createPreparedCollectionSelector,
  preparedStagingOrdersSelector,
  createRFQPanelIsPinnedSelector,
  preparedNegotiationInstructionSelector
} from 'selectors/selectionSelectors'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { defaultPrimeBrokerIdGetterSelector, tradingEntitiesGetterSelector } from 'selectors/tradingEntitySelectors'
import { getSettlementVenueOptions } from 'selectors/clearingRelationshipsBuilder'
import { Locale, Translation } from 'locale'
import { Decimal, primitive } from 'utils'
import { tradingRelationshipGetterSelector } from 'selectors/tradingRelationshipSelectors'
import { RepositoryActions } from 'store/repository'
import { uniq } from 'lodash-es'
import {
  OrderHaircutFieldName,
  OrderHaircutTypeFieldName,
  OrderMasterLegalAgreementTypeFieldName,
  OrderRepoTypeFieldName
} from 'epics/orderModifyEpic'
import { ModalConsumerProps, NotificationType, withModal } from 'view/shared/modal'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { ContentWrapper, RFQPrepareContainer, ButtonsSection } from './rfqPanelStyles'
import { hasCorporateCollateralValidationsErrors, hasGfidValidationErrors } from './rfqPanelUtils'

export interface SendRFQPanelProps extends ModalConsumerProps {
  isPinned: boolean
  ordersToPrepare: PreparedStagingOrder[]
  negotiationInstruction: NegotiationInstruction
  collectionToPrepare: Collection | null
  userPreference: UserPreference
  getTradingEntity: (entityId: string) => TradingEntity | null
  getTradingEntities: (entityIds: string[]) => TradingEntity[] | null
  getDefaultPrimeBrokerId: (ownerTradingEntityId: string, subjectTradingEntityId: string) => string | null
  onBulkPrepareRFQ: (args: OrderBulkPrepareArgs) => unknown
  onPrepareCollection: (args: OrderPrepareCollectionArgs) => unknown
  onUpdatePreparedStagingOrders: (preparedStagingOrders: PreparedStagingOrder[]) => unknown
  onUnprepareAll: () => unknown
  onUnprepare: (orderIds: string[]) => unknown
  onModifyCollectionType: (args: CollectionModifyTypeArgs) => unknown
  onTogglePinned: (isOn: boolean) => unknown
  getTradingRelationship: (buyTradingEntityId: string, sellTradingEntityId: string) => TradingRelationship | null
  onUpdateOrderField: (entityType: EntityType, entityId: string, fieldName: string, fieldValue: primitive | null) => unknown
}

const mapStateToProps = createStructuredSelector({
  isPinned: createRFQPanelIsPinnedSelector(),
  negotiationInstruction: preparedNegotiationInstructionSelector,
  ordersToPrepare: preparedStagingOrdersSelector,
  collectionToPrepare: createPreparedCollectionSelector(),
  userPreference: createUserPreferenceSelector(),
  getDefaultPrimeBrokerId: defaultPrimeBrokerIdGetterSelector,
  getTradingRelationship: tradingRelationshipGetterSelector,
  getTradingEntity: createEntityGetterSelector(EntityType.TRADING_ENTITY),
  getTradingEntities: tradingEntitiesGetterSelector
})

const mapDispatchToProps = {
  onBulkPrepareRFQ: OrderActions.bulkPrepare,
  onPrepareCollection: OrderActions.prepareCollection,
  onUpdatePreparedStagingOrders: SelectionActions.setPreparedStagingOrders,
  onUnprepareAll: SelectionActions.unprepareStagingAll,
  onUnprepare: SelectionActions.unprepareStagingOrders,
  onModifyCollectionType: CollectionActions.modifyType,
  onTogglePinned: (isOn: boolean) => SelectionActions.pinPrepareRFQPanel(isOn),
  onUpdateOrderField: RepositoryActions.updateField
}

export class SendRFQPanelComponent extends React.Component<SendRFQPanelProps> {
  constructor(props: SendRFQPanelProps) {
    super(props)
  }

  render() {
    const {
      negotiationInstruction: { csd, settlementVenue, clearingHouseId, sponsorId, rfqDurationMins, counterpartyInstructions },
      ordersToPrepare,
      collectionToPrepare,
      onUnprepareAll,
      isPinned,
      userPreference,
      getTradingEntity,
      getTradingEntities,
      onUnprepare,
      onTogglePinned
    } = this.props

    const isBasket = !!ordersToPrepare[0]?.orderDetail.order.basketAllocation
    const panelTitle = isBasket ? Locale.translate(Translation.TC_PREPARE_BASKET) : Locale.translate(Translation.TC_PREPARE_RFQ)
    const counterpartyIds = counterpartyInstructions.map(instruction => instruction.counterpartyId)
    const counterparties = getTradingEntities(counterpartyIds)

    const readOnly =
      ordersToPrepare.length === 0 ||
      !ordersToPrepare.every(preparedOrderDetail => preparedOrderDetail.orderDetail.availableEvents.includes(OrderEvent.PREPARE))

    // All the orders will share the same clearing house / sponsor combinations (if any), differing only in the Swift message id used later
    // so just pass the first one down for the droplist selections
    const preparedOrderClearingDetailsFromFirstOrder = ordersToPrepare.length > 0 ? ordersToPrepare[0].clearingDetails : []
    const basketType = (ordersToPrepare.length > 0 && ordersToPrepare[0].orderDetail.productSector?.basketType) || null

    const someOrdersAreOpenRepo = ordersToPrepare.some(
      preparedOrder =>
        preparedOrder.orderDetail.order.repoClassification === RepoClassification.OPEN ||
        preparedOrder.orderDetail.order.repoClassification === RepoClassification.OPEN_EVERGREEN
    )
    const someOrdersAreBlockGcBespoke = ordersToPrepare.some(
      preparedOrder =>
        preparedOrder.orderDetail.order.blockTrade &&
        preparedOrder.orderDetail.productSector?.basketType === CollateralBasketType.GC_BESPOKE
    )
    const someOrdersAreCallable = ordersToPrepare.some(preparedOrder => preparedOrder.orderDetail.order.callableType !== CallableType.NONE)
    const allOrdersAreUs = ordersToPrepare.every(preparedOrder => preparedOrder.orderDetail.order.market === Market.US)
    const orderIsStockBorrowingLending =
      isBasket && ordersToPrepare.some(preparedOrder => preparedOrder.orderDetail.order.repoType === RepoType.STOCK_BORROWING_LENDING)

    const orderIsGcPlus = ordersToPrepare.some(
      preparedOrder => preparedOrder.orderDetail.productSector?.basketType === CollateralBasketType.GC_PLUS
    )
    const someOrdersAreCorp = ordersToPrepare.some(preparedOrder => preparedOrder.orderDetail.collateral?.assetType === AssetType.CORP)

    const settlementVenueOptions = getSettlementVenueOptions(
      preparedOrderClearingDetailsFromFirstOrder,
      basketType,
      someOrdersAreOpenRepo,
      someOrdersAreBlockGcBespoke,
      someOrdersAreCallable,
      allOrdersAreUs,
      orderIsStockBorrowingLending,
      orderIsGcPlus,
      someOrdersAreCorp
    )

    const actionButtonTooltip =
      Decimal.multiply(ordersToPrepare.length, counterpartyInstructions.length) <= MAX_BULK_PREPARE_ACTION_SIZE
        ? ''
        : Locale.translate(Translation.SC_MAX_ORDERS_PREPARE_TOOLTIP, { maxPrepare: MAX_BULK_PREPARE_ACTION_SIZE })

    return ordersToPrepare.length || isPinned ? (
      <ContentWrapper>
        <SendRFQPanelTitle title={panelTitle} isPinned={isPinned} onTogglePinned={() => onTogglePinned(!isPinned)} />
        <RFQPrepareContainer>
          <AssetsSection readOnly={readOnly} selectedOrders={ordersToPrepare} userPreference={userPreference} onRemoveAsset={onUnprepare} />
          <RFQDetailsSection
            readOnly={readOnly}
            durationMinutes={rfqDurationMins}
            settlementVenue={settlementVenue}
            settlementVenueOptions={settlementVenueOptions}
            clearingHouseId={clearingHouseId}
            sponsorId={sponsorId}
            csd={csd}
            preparedOrderClearingDetails={preparedOrderClearingDetailsFromFirstOrder}
            preparedCollectionType={collectionToPrepare?.type ?? null}
            allOrdersAreUs={allOrdersAreUs}
            settlementVenueReadOnly={settlementVenueOptions.includes(SettlementVenue.Triparty) || someOrdersAreOpenRepo}
            onChangeNegotiationDetails={this.onChangeNegotiationDetails}
            onChangeCollectionType={this.onChangeCollectionType}
          />
          <CounterpartySection readOnly={readOnly} />
          <PositionsSection
            readOnly={readOnly}
            ordersToPrepare={ordersToPrepare}
            userPreference={userPreference}
            getTradingEntity={getTradingEntity}
            onChangePosition={this.onChangePosition}
            counterpartyInstructions={counterpartyInstructions}
          />
        </RFQPrepareContainer>
        <ButtonsSection>
          <DialogRow>
            <DialogField>
              <TextButton dataTestId={'SendRFQPanel-TextButton-ButtonCancel'} type={TextButtonType.Tertiary} onClick={onUnprepareAll}>
                {Locale.translate(Translation.TC_BUTTON_CANCEL)}
              </TextButton>
            </DialogField>
            <DialogField flex={0}>
              <Tooltip tooltip={actionButtonTooltip}>
                <div>
                  <TextButton
                    dataTestId={'SendRFQPanel-TextButton-ButtonSave'}
                    type={TextButtonType.Tertiary}
                    onClick={this.onSaveRFQs}
                    disabled={!this.canSaveRFQs(counterparties)}>
                    {Locale.translate(Translation.TC_BUTTON_SAVE)}
                  </TextButton>
                </div>
              </Tooltip>
            </DialogField>
            <DialogField flex={0}>
              <Tooltip tooltip={actionButtonTooltip}>
                <div>
                  <TextButton
                    dataTestId={'SendRFQPanel-TextButton-ButtonSendRFQ'}
                    type={TextButtonType.Primary}
                    onClick={this.onSendRFQs}
                    disabled={!this.canSendRFQs(counterparties)}>
                    {Locale.translate(Translation.TC_BUTTON_SEND_RFQ)}
                  </TextButton>
                </div>
              </Tooltip>
            </DialogField>
          </DialogRow>
        </ButtonsSection>
      </ContentWrapper>
    ) : null
  }

  private onChangeNegotiationDetails = (partialNegotiationInstruction: Partial<NegotiationInstruction>) => {
    const newPreparedStagingOrders = this.props.ordersToPrepare.map<PreparedStagingOrder>(orderToPrepare => ({
      ...orderToPrepare,
      negotiationInstruction: {
        ...orderToPrepare.negotiationInstruction,
        ...partialNegotiationInstruction,
        hideCompetition: false
      }
    }))
    this.props.onUpdatePreparedStagingOrders(newPreparedStagingOrders)
  }

  private onChangeCollectionType = (collectionType: CollectionType) => {
    const { collectionToPrepare, onModifyCollectionType } = this.props
    if (collectionToPrepare) {
      onModifyCollectionType({
        collectionId: collectionToPrepare.id,
        collectionType
      })
    }
  }

  private onChangePosition = (orderIds: string[], counterpartyIds: string[], isAxed: boolean, tradeConditionFlag: TradeConditionFlag) => {
    const newPreparedStagingOrders = this.props.ordersToPrepare.map(orderToPrepare => {
      const ordersAreNotModified = orderIds.every(id => id !== orderToPrepare.orderDetail.order.orderId)
      if (ordersAreNotModified) {
        return orderToPrepare
      }

      const counterpartyInstructions = orderToPrepare.negotiationInstruction.counterpartyInstructions.map(counterpartyInstruction => {
        const counterpartiesAreNotModified = counterpartyIds.every(id => id !== counterpartyInstruction.counterpartyId)

        if (counterpartiesAreNotModified) {
          return counterpartyInstruction
        }

        return {
          ...counterpartyInstruction,
          isAxed,
          axeId: isAxed ? counterpartyInstruction.axeId : null,
          tradeConditionFlag
        }
      })

      return {
        ...orderToPrepare,
        negotiationInstruction: {
          ...orderToPrepare.negotiationInstruction,
          counterpartyInstructions
        }
      }
    })
    this.props.onUpdatePreparedStagingOrders(newPreparedStagingOrders)
  }

  private clearingIsValid = (negotiationInstruction: NegotiationInstruction) => {
    // TODO add tests for predicates when clearing house id and sponsor id are stored in NegotiationInstruction
    const { settlementVenue, clearingHouseId, sponsorId } = negotiationInstruction
    const { ordersToPrepare } = this.props

    if (settlementVenue === SettlementVenue.Bilateral || settlementVenue === SettlementVenue.Triparty) {
      return true
    }

    if (settlementVenue === SettlementVenue.Direct) {
      return clearingHouseId !== null
    }

    if (settlementVenue === SettlementVenue.Sponsored) {
      const allOrdersAreUs = ordersToPrepare.every(preparedOrder => preparedOrder.orderDetail.order.market === Market.US)
      return clearingHouseId !== null && (allOrdersAreUs || sponsorId !== null)
    }

    return false
  }

  private hasCollectionAndIsValid = () => {
    const { collectionToPrepare } = this.props
    return !collectionToPrepare || (!!collectionToPrepare.type && !!collectionToPrepare.name)
  }

  private canSendRFQs = (counterparties: TradingEntity[] | null) => {
    const { ordersToPrepare } = this.props
    const { counterpartyInstructions, rfqDurationMins } = this.props.negotiationInstruction
    const allSubmittable = ordersToPrepare.every(orderToPrepare => orderToPrepare.orderDetail.availableEvents.includes(OrderEvent.SUBMIT))

    return (
      !hasCorporateCollateralValidationsErrors(ordersToPrepare, counterpartyInstructions) &&
      !hasGfidValidationErrors(ordersToPrepare, counterparties) &&
      this.clearingIsValid(this.props.negotiationInstruction) &&
      this.hasCollectionAndIsValid() &&
      counterpartyInstructions.length !== 0 &&
      Decimal.multiply(ordersToPrepare.length, counterpartyInstructions.length) <= MAX_BULK_PREPARE_ACTION_SIZE &&
      rfqDurationMins !== 0 &&
      rfqDurationMins < 1440 && // 1 day
      allSubmittable
    )
  }

  private canSaveRFQs = (counterparties: TradingEntity[] | null) => {
    const { ordersToPrepare } = this.props
    const { counterpartyInstructions, rfqDurationMins } = this.props.negotiationInstruction
    const allSaveable = ordersToPrepare.every(orderToPrepare => orderToPrepare.orderDetail.availableEvents.includes(OrderEvent.PREPARE))

    return (
      !hasCorporateCollateralValidationsErrors(ordersToPrepare, counterpartyInstructions) &&
      !hasGfidValidationErrors(ordersToPrepare, counterparties) &&
      this.clearingIsValid(this.props.negotiationInstruction) &&
      this.hasCollectionAndIsValid() &&
      counterpartyInstructions.length !== 0 &&
      Decimal.multiply(ordersToPrepare.length, counterpartyInstructions.length) <= MAX_BULK_PREPARE_ACTION_SIZE &&
      rfqDurationMins !== 0 &&
      rfqDurationMins < 1440 && // 1 day
      allSaveable
    )
  }

  private onSendRFQs = () => {
    this.processRFQs(true)
  }

  private onSaveRFQs = () => {
    this.processRFQs(false)
  }

  private processCollection = async (submitOrder: boolean) => {
    const { collectionToPrepare, ordersToPrepare, onPrepareCollection, showDialog } = this.props
    if (!collectionToPrepare) {
      throw new Error('No collection to be prepared')
    }
    const prepareCollectionArgs = this.createPrepareCollectionArgs(submitOrder, collectionToPrepare.id)
    if (
      submitOrder &&
      collectionToPrepare.isCurve &&
      ordersToPrepare.some(
        orderToPrepare =>
          (orderToPrepare.orderDetail.order.notional === null &&
            orderToPrepare.orderDetail.order.startConsideration !== ordersToPrepare[0].orderDetail.order.startConsideration) ||
          (orderToPrepare.orderDetail.order.startConsideration === null &&
            orderToPrepare.orderDetail.order.notional !== ordersToPrepare[0].orderDetail.order.notional) ||
          (orderToPrepare.orderDetail.order.notional !== ordersToPrepare[0].orderDetail.order.notional &&
            orderToPrepare.orderDetail.order.startConsideration !== ordersToPrepare[0].orderDetail.order.startConsideration)
      )
    ) {
      const dialogArgs = {
        type: NotificationType.Confirm,
        title: Translation.TC_TREAT_AS_CURVE,
        message: Translation.SC_TREAT_AS_CURVE_CONFIRMATION,
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        cancelButton: Translation.TC_BUTTON_NO,
        defaultButton: Translation.TC_BUTTON_YES
      }
      const dialogResult = await showDialog(dialogArgs)

      onPrepareCollection({ ...prepareCollectionArgs, forcedIsCurve: dialogResult === Translation.TC_BUTTON_YES })
    } else {
      onPrepareCollection(prepareCollectionArgs)
    }
  }

  private processOrders = (submitOrder: boolean) => {
    const { onUpdateOrderField, onBulkPrepareRFQ } = this.props
    const bulkSubmitArgs = this.createBulkPrepareArgs(submitOrder)

    const updatedOrderCpInstructions: OrderCounterpartyInstructions[] = bulkSubmitArgs.orderCounterpartyInstructions.map(
      orderCounterpartyInstructions => {
        const uniqueMlas = uniq(
          orderCounterpartyInstructions.counterpartyInstructions.map(
            counterpartyInstruction => counterpartyInstruction.masterLegalAgreementType
          )
        )

        const uniqueRepoTypes = uniq(
          orderCounterpartyInstructions.counterpartyInstructions.map(counterpartyInstruction => counterpartyInstruction.repoType)
        )

        if (uniqueMlas.length === 1 && uniqueMlas[0] !== null) {
          // Back apply the master legal agreement onto the underlying order if the defaulting
          // has resulted in all counterparty MLA values being the same
          onUpdateOrderField(EntityType.ORDER, orderCounterpartyInstructions.orderId, OrderMasterLegalAgreementTypeFieldName, uniqueMlas[0])
        }

        if (uniqueRepoTypes.length === 1 && uniqueRepoTypes[0] !== null) {
          // Back apply the repo type onto the underlying order if the defaulting
          // has resulted in all counterparty repo type values being the same
          onUpdateOrderField(EntityType.ORDER, orderCounterpartyInstructions.orderId, OrderRepoTypeFieldName, uniqueRepoTypes[0])
        }
        const settlementVenue = bulkSubmitArgs.negotiationInstruction.settlementVenue
        if (settlementVenue === SettlementVenue.Direct || settlementVenue === SettlementVenue.Sponsored) {
          onUpdateOrderField(EntityType.ORDER, orderCounterpartyInstructions.orderId, OrderHaircutFieldName, null)
          onUpdateOrderField(EntityType.ORDER, orderCounterpartyInstructions.orderId, OrderHaircutTypeFieldName, null)
        }

        return {
          ...orderCounterpartyInstructions,
          counterpartyInstructions: orderCounterpartyInstructions.counterpartyInstructions.map(counterpartyInstruction => ({
            ...counterpartyInstruction,
            ...(uniqueMlas.length === 1 && uniqueMlas[0] !== null && { masterLegalAgreementType: null }),
            ...(uniqueRepoTypes.length === 1 && uniqueRepoTypes[0] !== null && { repoType: null })
          }))
        }
      }
    )

    onBulkPrepareRFQ({
      ...bulkSubmitArgs,
      orderCounterpartyInstructions: updatedOrderCpInstructions
    })
  }

  private processRFQs = async (submitOrder: boolean) => {
    const { collectionToPrepare, onUnprepareAll } = this.props

    if (collectionToPrepare?.id && collectionToPrepare.type && collectionToPrepare.type !== CollectionType.SEND_INDIVIDUALLY) {
      this.processCollection(submitOrder)
    } else {
      this.processOrders(submitOrder)
    }

    onUnprepareAll()
  }

  private createPrepareCollectionArgs(submitOrder: boolean, collectionId: string): OrderPrepareCollectionArgs {
    const negotiationInstruction =
      submitOrder && this.props.negotiationInstruction.counterpartyInstructions.length <= 1
        ? { ...this.props.negotiationInstruction, hideCompetition: null }
        : this.props.negotiationInstruction

    return {
      collectionId,
      orderCounterpartyInstructions: this.createOrderCounterpartyInstructions(),
      negotiationInstruction,
      submitOrder,
      forcedIsCurve: null
    }
  }

  private createBulkPrepareArgs(submitOrder: boolean): OrderBulkPrepareArgs {
    const negotiationInstruction =
      submitOrder && this.props.negotiationInstruction.counterpartyInstructions.length <= 1
        ? { ...this.props.negotiationInstruction, hideCompetition: null }
        : this.props.negotiationInstruction

    return {
      orderCounterpartyInstructions: this.createOrderCounterpartyInstructions(),
      negotiationInstruction,
      submitOrder
    }
  }

  private createOrderCounterpartyInstructions(): OrderCounterpartyInstructions[] {
    const { ordersToPrepare } = this.props
    return ordersToPrepare.map(preparedStagingOrder => {
      return {
        orderId: preparedStagingOrder.orderDetail.order.orderId,
        counterpartyInstructions: this.getCounterpartyInstructions(preparedStagingOrder)
      }
    })
  }

  private getCounterpartyInstructions(preparedStagingOrder: PreparedStagingOrder): CounterpartyInstruction[] {
    const { getTradingRelationship } = this.props
    const {
      negotiationInstruction,
      orderDetail: {
        order: { fundId, masterLegalAgreementType: orderMasterLegalAgreementType }
      }
    } = preparedStagingOrder

    return negotiationInstruction.counterpartyInstructions.map(counterpartyInstruction => {
      const tradingRelationship = fundId ? getTradingRelationship(fundId, counterpartyInstruction.counterpartyId) : null
      const defaultMla = !orderMasterLegalAgreementType && tradingRelationship ? tradingRelationship.defaultAgreement : null

      return {
        ...counterpartyInstruction,
        masterLegalAgreementType: defaultMla
      }
    })
  }
}

export const SendRFQPanel = withModal(connect(mapStateToProps, mapDispatchToProps)(SendRFQPanelComponent))
